import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaEdit, FaPlus, FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner.jsx';
import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import {
  STREAM_SOURCES,
  channelPageUrl,
  isSelectableStreamSource,
  normalizeChannelId,
  normalizeStreamSource,
  streamSourceLabel,
  streamSourceSaveError,
} from '../../_helpers/streamSource.js';
import '../../styles/partial/StreamSourceBadge.css';
import '../../styles/partial/StreamSourceSetup.css';

const EMPTY_FORM = { source: STREAM_SOURCES[0].value, channelId: '' };

/**
 * Band Settings → Stream Source. A band has at most one source
 * (`{ source, channel_id }`): read from `GET /public/bands/{id}/stream/source`
 * (404 when none), written with `PUT /bands/{id}/stream/source` and removed
 * with `DELETE /bands/{id}/stream/source`. Listeners get the player URL the
 * server builds from it on `/public/bands/{id}/stream/status`.
 *
 * A YouTube @handle is sent as typed; the server resolves it to the channel
 * ID before storing (see streamSource.js), so the panel re-reads the source
 * after saving to show what was actually stored.
 *
 * While a save is in flight the form is locked: Cancel and the fields are
 * disabled. The PUT cannot be taken back once sent - with a handle it holds
 * the server's lookup (up to several seconds) and then writes - so a Cancel
 * that closed the form would leave the read-only view showing a source the
 * server has since replaced, and a save that settled afterwards would land on
 * whatever the member was doing next. The outcome, either way, lands in the
 * form that sent it.
 */
function StreamSourceSetup({ band, onBack }) {
  const bandId = band?.ID ?? band?.id;
  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();

  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  // Resolves to the configured source, or null on the 404 that means "none".
  const loadSource = useCallback(async () => {
    try {
      const data = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/public/bands/${bandId}/stream/source`,
      );
      return normalizeStreamSource(data);
    } catch (err) {
      if (err?.status === 404) return null;
      throw err;
    }
  }, [bandId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    loadSource()
      .then(loaded => { if (!cancelled) setSource(loaded); })
      .catch(() => {
        if (!cancelled) {
          setSource(null);
          setError(t('streamSource.errors.loadFailed'));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadSource]);

  const flashSuccess = (message, durationMs = 3000) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), durationMs);
  };

  const handleStartEdit = () => {
    // A stored source the panel cannot author (songshake) is shown as-is but
    // edited as a fresh Twitch/YouTube entry, so a radio is always selected
    // and the channel field never carries an identifier for the wrong platform.
    setForm(
      source && isSelectableStreamSource(source.source)
        ? { source: source.source, channelId: source.channelId }
        : EMPTY_FORM,
    );
    setError('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    const normalized = normalizeChannelId(form.source, form.channelId, t);
    if (normalized.error) {
      setError(normalized.error);
      return;
    }
    setError('');
    setSaving(true);
    try {
      // A YouTube @handle goes as typed: the server looks the channel ID up
      // (the spinner covers that too) and stores only the ID. A miss comes
      // back as a 400 and leaves the form open with the handle still in the
      // field so it can be corrected.
      const channelId = normalized.value ?? normalized.handle;
      await fetchWrapperRef.current.put(
        `${config.apiBaseUrl}/bands/${bandId}/stream/source`,
        { source: form.source, channel_id: channelId },
      );
      // PUT answers 204; re-read so the panel shows exactly what was stored
      // (for a handle, the channel ID it resolved to), falling back to what
      // was sent if that read fails.
      const stored = await loadSource().catch(() => null);
      setSource(stored ?? { source: form.source, channelId });
      setIsEditing(false);
      setForm(EMPTY_FORM);
      // After a handle the panel shows a channel ID, so say where it came
      // from - or, when the re-read failed and it is showing the handle, that
      // the server holds the ID. Either needs longer on screen than "saved".
      if (!normalized.handle) {
        flashSuccess(t('streamSource.success.saved'));
      } else if (stored?.channelId) {
        flashSuccess(t('streamSource.success.savedHandleResolved', { handle: normalized.handle, channelId: stored.channelId }), 8000);
      } else {
        flashSuccess(t('streamSource.success.savedHandleUnresolved', { handle: normalized.handle }), 8000);
      }
    } catch (err) {
      setError(streamSourceSaveError(err, normalized.handle, t));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('streamSource.confirm.remove'))) return;
    setError('');
    try {
      await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/bands/${bandId}/stream/source`);
      setSource(null);
      flashSuccess(t('streamSource.success.removed'));
    } catch {
      setError(t('streamSource.errors.removeFailed'));
    }
  };

  const selectedPlatform = STREAM_SOURCES.find(entry => entry.value === form.source) ?? STREAM_SOURCES[0];
  const channelUrl = source ? channelPageUrl(source.source, source.channelId) : null;

  const selectPlatform = (value) => setForm(prev => ({ ...prev, source: value }));

  // WAI-ARIA radio group: arrows move selection (wrapping), Home/End jump,
  // and only the selected radio is in the tab order.
  const handlePlatformKeyDown = (event) => {
    const index = STREAM_SOURCES.findIndex(entry => entry.value === selectedPlatform.value);
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % STREAM_SOURCES.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + STREAM_SOURCES.length) % STREAM_SOURCES.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = STREAM_SOURCES.length - 1;
    if (next === null) return;
    event.preventDefault();
    selectPlatform(STREAM_SOURCES[next].value);
    event.currentTarget.querySelectorAll('[role="radio"]')[next]?.focus();
  };

  return (
    <div className="stream-source-setup">
      <div className="band-settings-header">
        <button className="band-settings-back-button" onClick={onBack}>
          <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
        </button>
        <h2 className="band-settings-title">{t('streamSource.title')}</h2>
      </div>

      {error && (
        <div className="message error-message" role="alert">
          <FaTimes className="message-icon" aria-hidden="true" /> {error}
        </div>
      )}
      {successMessage && (
        <div className="message success-message">✓ {successMessage}</div>
      )}

      <div className="band-settings-description stream-source-description">
        <p>
          {t('streamSource.description')}
        </p>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="stream-source-content">
          {!isEditing && (
            source ? (
              <div className="stream-source-item" role="group" aria-label={t('streamSource.current.groupLabel')}>
                <div className="stream-source-item-info">
                  <span className="stream-source-type-badge">{streamSourceLabel(source.source, t)}</span>
                  <span className="stream-source-name">{source.channelId}</span>
                  {channelUrl && (
                    <a
                      className="stream-source-url"
                      href={channelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={channelUrl}
                    >
                      {channelUrl}
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  className="small-button stream-source-edit-btn"
                  onClick={handleStartEdit}
                  aria-label={t('streamSource.current.changeLabel')}
                  title={t('streamSource.current.changeTitle')}
                >
                  <FaEdit /> {t('streamSource.current.change')}
                </button>
                <button
                  type="button"
                  className="remove-button small-button"
                  onClick={handleDelete}
                  aria-label={t('streamSource.current.removeLabel')}
                  title={t('streamSource.current.removeTitle')}
                >
                  <FaTrash />
                </button>
              </div>
            ) : (
              <>
                <p className="stream-source-empty">{t('streamSource.empty')}</p>
                <button
                  type="button"
                  className="small-button add-button stream-source-add-btn"
                  onClick={handleStartEdit}
                >
                  <FaPlus /> {t('streamSource.actions.set')}
                </button>
              </>
            )
          )}

          {isEditing && (
            <div className="stream-source-add-form">
              <div className="form-section">
                <span className="form-label">{t('streamSource.form.platformLabel')}</span>
                <div
                  className="stream-source-type-selector"
                  role="radiogroup"
                  aria-label={t('streamSource.form.platform')}
                  onKeyDown={handlePlatformKeyDown}
                >
                  {STREAM_SOURCES.map(platform => (
                    <button
                      key={platform.value}
                      type="button"
                      role="radio"
                      aria-checked={selectedPlatform.value === platform.value}
                      tabIndex={selectedPlatform.value === platform.value ? 0 : -1}
                      className={`stream-source-type-btn ${selectedPlatform.value === platform.value ? 'active' : ''}`}
                      disabled={saving}
                      onClick={() => selectPlatform(platform.value)}
                    >
                      <strong>{t(platform.labelKey)}</strong>
                      <span>{t(platform.descriptionKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">
                  {t('streamSource.form.channelLabel', { channel: t(selectedPlatform.channelLabelKey) })}
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t(selectedPlatform.placeholderKey)}
                    value={form.channelId}
                    disabled={saving}
                    onChange={e => setForm(prev => ({ ...prev, channelId: e.target.value }))}
                  />
                </label>
              </div>

              <div className="form-buttons">
                <button
                  type="button"
                  className="form-button cancel-button"
                  disabled={saving}
                  onClick={handleCancelEdit}
                >
                  <FaTimes /> {t('common.actions.cancel')}
                </button>
                {saving ? (
                  <LoadingSpinner />
                ) : (
                  <button type="button" className="form-button save-button" onClick={handleSave}>
                    <FaSave /> {t('streamSource.actions.save')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StreamSourceSetup;
